import { Component, Input, OnInit } from '@angular/core';
import { User, UsersService, AuthStatusResponse } from '../../../shared/services/users/users.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { UserRoleService, UserRole } from '../../../shared/services/user-role/user-role.service';
import { AuthService } from '../../../shared/services/auth/auth.service';

@Component({
  selector: 'app-user-detail-access',
  templateUrl: './user-detail-access.component.html',
  styleUrls: ['./user-detail-access.component.scss']
})
export class UserDetailAccessComponent implements OnInit {
  @Input() user!: User;
  authStatus: AuthStatusResponse | null = null;
  isLoading = false;
  hasError = false;
  resending = false;

  // Define the onboarding steps
  steps = [
    { id: 'is_started', label: 'Account Created' },
    { id: 'is_in_progress', label: 'Setup In Progress' },
    { id: 'is_complete', label: 'Setup Complete' }
  ];

  UserRole = UserRole; // Expose enum to template

  constructor(
    private usersService: UsersService,
    private toaster: ToasterService,
    public userRoleService: UserRoleService, // Make public for template access
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.loadAuthStatus();
  }

  /**
   * Load the user's authentication status
   */
  async loadAuthStatus(): Promise<void> {
    if (!this.user || !this.user.USER_ID) {
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    try {
      this.authStatus = await this.usersService.getUserAuthStatus(this.user.USER_ID);
      console.log(this.authStatus);
    } catch (error: any) {
      this.hasError = true;
      this.toaster.warning(`Error loading authentication status: ${error?.message || error}`, 4000);
      console.error('Error loading authentication status:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Check if a step is completed based on the auth status
   */
  isStepCompleted(stepId: string): boolean {
    if (!this.authStatus) return false;
    
    switch (stepId) {
      case 'is_started':
        return this.authStatus.is_started;
      case 'is_in_progress':
        return this.authStatus.is_in_progress;
      case 'is_complete':
        return this.authStatus.is_complete;
      default:
        return false;
    }
  }

  /**
   * Format the date for display
   */
  formatDate(date: Date | null | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Resend the invitation email to the user if they have not logged in yet
   */
  async resendInvitation(): Promise<void> {
    if (!this.user?.EMAIL) {
      this.toaster.warning('No email available for this user.', 3000);
      return;
    }

    this.resending = true;
    try {
      const ok = await this.authService.resendInvitation(this.user.EMAIL);
      if (ok) {
        this.toaster.success('Invitation email resent successfully.', 4000);
      } else {
        this.toaster.warning('Failed to resend invitation email.', 4000);
      }
    } catch (error: any) {
      this.toaster.warning(`Error resending invitation: ${error?.message || error}`, 4000);
    } finally {
      this.resending = false;
    }
  }
}
